<template>
  <div class="sorting" @keydown="onKeydown">
    <button
      class="sorting-header"
      @click="toggleExpander($event)"
      aria-haspopup="listbox"
      :aria-expanded="expanderVisible ? 'true' : 'false'"
      :aria-controls="'sorting-listbox'"
    >
      <span class="sorting-selected">{{ selectedSorting && selectedSorting.name }}</span>
      <i class="sorting-icon icon-chevron-down"></i>
    </button>
    <expander :expanded="expanderVisible">
      <ul v-if="availableSortingOptions && selectedSorting" :id="'sorting-listbox'" role="listbox">
        <li v-for="option in availableSortingOptions" :key="option.name">
          <button
            @click.prevent="sort(option)"
            :class="option.name === selectedSorting.name ? 'selected' : ''"
            role="option"
            :id="'sorting-option-' + option.name"
            :aria-selected="option.name === selectedSorting.name ? 'true' : 'false'"
          >
            {{ option.name }}
          </button>
        </li>
      </ul>
    </expander>
  </div>
</template>

<script lang="ts">
import config from '@/config';
import Expander from '../expander/expander.vue';
import { defineComponent, type PropType } from 'vue';
import type { SortingOption } from '@/config/sorting';
import { getFeatureGroupById } from '@/config/featureGroups';
import type { SortingOptionIdsType } from '@/config/featureGroups';
import logger from '@/app/logger';
import type { FeatureGroupIdType } from '@/types/schemaTypes';

const { SORTING_OPTIONS } = config;

export default defineComponent({
  name: 'sorting',
  props: {
    orderBy: {
      type: String,
      required: false,
    },
    order: {
      type: String,
      required: false,
    },
    mode: {
      type: String,
      required: true,
    },
    featureGroup: {
      type: String as PropType<FeatureGroupIdType>,
      required: true,
    },
  },
  data: () => ({
    expanderVisible: false,
  }),
  mounted() {
    this.$bus.$on(
      'collapseExpanders',
      () => {
        this.expanderVisible = false;
      },
      this
    );
  },
  methods: {
    toggleExpander(event: MouseEvent) {
      this.expanderVisible = !this.expanderVisible;
      event.stopPropagation();
    },
    sort(sorting: SortingOption) {
      this.expanderVisible = false;
      this.$emit('sortingChanged', sorting);
    },
    onKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.expanderVisible = false;
      }
    },
  },
  computed: {
    availableSortingOptions(): SortingOption[] {
      const featureGroup = getFeatureGroupById(this.featureGroup);
      const ids = featureGroup?.sortingOptionIds[this.mode as keyof SortingOptionIdsType];
      if (!ids) {
        logger.error(`No sorting options found for feature group "${this.featureGroup}"`);
      }
      return ids ? SORTING_OPTIONS.filter((option) => ids.includes(option.id)) : [];
    },
    selectedSorting() {
      const order = this.order || config.FILTER_DEFAULTS.order;
      const orderBy = this.orderBy || config.FILTER_DEFAULTS.orderBy;
      const sel = SORTING_OPTIONS.find((opt) => opt.orderBy === orderBy && opt.order === order);
      return sel;
    },
  },
  components: {
    Expander,
  },
});
</script>
